import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useQuery} from '@github-ui/react-query'
import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {sendStats} from '@github-ui/stats'
import {useEffect, useState} from 'react'
import type {CIFinding} from './ci-findings-types'

type CIFindingsAPIResponse = {
  ciFindings: CIFinding[]
  checkRunId?: number
  checkSuiteId?: number
  pullRequestId?: number
  headSha?: string
}

export const POLL_INTERVAL_MS = 10_000
export const MAX_POLL_ATTEMPTS = 30 // Stop polling after ~5 minutes (30 × 10s)
const POLL_TIMEOUT_MS = MAX_POLL_ATTEMPTS * POLL_INTERVAL_MS

export function useCIFindings({
  enabled = true,
  pollingTimeoutMs = POLL_TIMEOUT_MS,
}: {enabled?: boolean; pollingTimeoutMs?: number} = {}): {
  findings: CIFinding[]
  isPolling: boolean
  isError: boolean
} {
  const apiURL = usePageDataUrl(PageData.ciFindings)
  const [pollingExpired, setPollingExpired] = useState(false)

  const {data, isError} = useQuery({
    queryKey: ['ciFindings', apiURL],
    queryFn: async (): Promise<CIFinding[]> => {
      const response = await fetchWithErrorHandling(apiURL)
      if (!response.ok) {
        throw new Error(`CI findings request failed with status ${response.status}`)
      }
      const json = (await parseJSONWithBetterErrors(response)) as CIFindingsAPIResponse
      return json.ciFindings ?? []
    },
    staleTime: 30_000,
    retry: false,
    enabled,
    refetchOnWindowFocus: false,
    refetchInterval: query => {
      if (query.state.data && query.state.data.length > 0) return false
      if (query.state.status === 'error') return false
      if (pollingExpired) return false
      return POLL_INTERVAL_MS
    },
  })

  const findings = data ?? []
  const shouldPoll = enabled && findings.length === 0 && !isError

  // Reset pollingExpired when polling conditions clear (set-state-during-render)
  if (!shouldPoll && pollingExpired) {
    setPollingExpired(false)
  }

  useEffect(() => {
    if (!shouldPoll) return
    const timer = setTimeout(() => setPollingExpired(true), pollingTimeoutMs)
    return () => clearTimeout(timer)
  }, [shouldPoll, pollingTimeoutMs])

  useEffect(() => {
    if (isError) {
      sendStats({incrementKey: 'CI_FINDINGS_FETCH_ERROR'})
    }
  }, [isError])

  // Polling is active when enabled, no findings, no error, and within the polling window
  const isPolling = shouldPoll && !pollingExpired

  return {findings, isPolling, isError}
}
